import { getCurrencySymbol } from '@vy/services';
import { DEFAULT_CURRENCY, DISCOUNT_MODELS, PRICING_MODELS, type FixedProduct } from '@vy/types';
import type { DiscountModel } from '@vy/types';
import { isEmptyString, isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { isNumber } from 'lodash';
import { InputAdornment } from '@mui/material';
import { useEffect, useState } from 'react';
import { useController, useFormContext, useWatch, type Control, type FieldPath } from 'react-hook-form';
import {
  VyStack,
  VySelect,
  VyTextField,
  VyToggleSection,
  VyTypography,
} from '@vy/ui-design-system';
import { withSuspenseBoundary } from '../../../../../../components/utility/SuspenseBoundary';
import { useChargeContext } from '../../charge-context';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { NewPlanForm } from '../../CreatePlanForm/create-plan-form-types/new-plan-form';
import { useFormErrorNotificationContext } from '../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { VyNumberFieldField, VySelectField, VyTextFieldField } from '../../../../../../design-system-adapters/forms';

const DISCOUNT_TYPE_OPTIONS: DiscountModel[] = [DISCOUNT_MODELS.PERCENTAGE, DISCOUNT_MODELS.FIXED];

function getDiscountModelLabel(model: DiscountModel): string {
  switch (model) {
    case DISCOUNT_MODELS.FIXED:
      return 'Fixed';
    case DISCOUNT_MODELS.PERCENTAGE:
      return 'Percentage (%)';
    default:
      return model;
  }
}

type DurationMode = 'entire' | 'specific';

const DURATION_MODE_OPTIONS: DurationMode[] = ['entire', 'specific'];

function getDurationModeLabel(mode: DurationMode): string {
  return mode === 'entire' ? 'For the entire contract' : 'Specific number of cycles';
}

type DiscountDurationFieldProps = {
  control: Control<PlanInputForm>;
  recurringPath: FieldPath<PlanInputForm>;
};

const DiscountDurationField: React.FC<DiscountDurationFieldProps> = ({ control, recurringPath }) => {
  const { field, fieldState } = useController({ control, name: recurringPath });
  const [durationMode, setDurationMode] = useState<DurationMode>(
    isNumber(field.value) ? 'specific' : 'entire',
  );

  const handleModeChange = (next: DurationMode) => {
    setDurationMode(next);
    if (next === 'specific') {
      if (!isNumber(field.value)) field.onChange(1);
    } else {
      field.onChange(undefined);
    }
  };

  const handleCyclesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isEmptyString(event.target.value)) {
      field.onChange(undefined);
      return;
    }
    const parsed = Number(event.target.value);
    field.onChange(Number.isNaN(parsed) ? undefined : Math.max(Math.trunc(parsed), 1));
  };

  const isEmptyCycles = durationMode === 'specific' && !isNumber(field.value);
  const hasError = isNotNullOrUndefined(fieldState.error) || isEmptyCycles;
  const errorMessage = fieldState.error?.message ?? (isEmptyCycles ? 'Enter the number of cycles' : undefined);

  return (
    <VyStack sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, alignItems: 'flex-start' }}>
      <VyStack sx={{ flex: 1, minWidth: 0 }}>
        <VySelect<DurationMode>
          label="Duration"
          options={DURATION_MODE_OPTIONS}
          getOptionLabel={getDurationModeLabel}
          value={durationMode}
          onChange={(_event, next) => handleModeChange(next ?? 'entire')}
          disableClearable
          data-vy="plan-charge-drawer-discount-duration"
        />
      </VyStack>
      {durationMode === 'specific' && (
        <VyStack sx={(theme) => ({ width: theme.spacing(12) })}>
          <VyTextField
            value={field.value ?? ''}
            onChange={handleCyclesChange}
            onBlur={field.onBlur}
            type="number"
            label="Cycles"
            error={hasError}
            helperText={errorMessage}
            slotProps={{ htmlInput: { min: 1, step: 1 } }}
            data-vy="plan-charge-drawer-discount-recurring"
          />
        </VyStack>
      )}
    </VyStack>
  );
};

const DiscountSectionInner: React.FC = () => {
  const { itemIndex: productIndex } = useChargeContext<FixedProduct>();
  const { registerErrorField } = useFormErrorNotificationContext();

  const discountConfigPath =
    `fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.discountConfiguration` as const;
  const discountTypePath = `${discountConfigPath}.type` as const;
  const amountToDeductPath = `${discountConfigPath}.amountToDeduct` as const;
  const recurringPath = `${discountConfigPath}.recurring` as const;
  const pricingModelPath = `fixedProducts.${productIndex}.variants.0.pricing.pricingModel` as const;

  const {
    control,
    getValues,
    setValue,
    watch,
  } = useFormContext<PlanInputForm, NewPlanForm>();

  const currency = DEFAULT_CURRENCY;
  const isSubscriptionProduct = getValues(pricingModelPath) === PRICING_MODELS.Subscription;

  useEffect(() => {
    registerErrorField(recurringPath);
  }, [registerErrorField, recurringPath]);

  // Drop a stale/partial discount (e.g. a zero-amount config persisted by the
  // pre-switch flow) on mount so a product without a real discount validates.
  useEffect(() => {
    const persistedAmount = getValues(amountToDeductPath);
    const persistedConfig = getValues(discountConfigPath);
    if (!isNullOrUndefined(persistedConfig) && !(isNumber(persistedAmount) && persistedAmount > 0)) {
      setValue(discountConfigPath, undefined);
    }
  }, [discountConfigPath, amountToDeductPath, getValues, setValue]);

  const discountConfig = useWatch({ control, name: discountConfigPath });
  const isEnabled = !isNullOrUndefined(discountConfig);
  const discountType = watch(discountTypePath);
  const adornmentSymbol = discountType === DISCOUNT_MODELS.PERCENTAGE ? '%' : getCurrencySymbol(currency);
  const amountLabel = discountType === DISCOUNT_MODELS.PERCENTAGE ? 'Discount (%)' : 'Discount';

  const handleToggle = (next: boolean) => {
    if (next) {
      setValue(
        discountConfigPath,
        { type: DISCOUNT_MODELS.PERCENTAGE },
        { shouldDirty: true },
      );
    } else {
      // Reset each subscribed field so the inputs visibly clear (setting only
      // the parent path to undefined doesn't re-render nested Controllers in
      // RHF), then drop the whole config so nothing partial lingers.
      setValue(`${discountConfigPath}.name`, undefined, { shouldDirty: true });
      setValue(discountTypePath, undefined, { shouldDirty: true });
      setValue(amountToDeductPath, undefined, { shouldDirty: true });
      setValue(recurringPath, undefined, { shouldDirty: true });
      setValue(discountConfigPath, undefined, { shouldDirty: true });
    }
  };

  return (
    <VyToggleSection
      title="Add Discount"
      description="Apply a percentage or fixed discount to this item"
      checked={isEnabled}
      onChange={handleToggle}
      data-vy="plan-charge-drawer-discount-section"
    >
      <VyStack sx={{ gap: 1.5 }}>
        <VyTypography variant="subtitle2">Discount Configuration</VyTypography>

        <VyStack
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            columnGap: 3,
            rowGap: 2,
          }}
        >
          <VySelectField<PlanInputForm, DiscountModel>
            name={discountTypePath}
            label="Discount Type"
            options={DISCOUNT_TYPE_OPTIONS}
            getOptionLabel={getDiscountModelLabel}
            data-vy="plan-charge-drawer-discount-type"
          />

          <VyNumberFieldField<PlanInputForm>
            name={amountToDeductPath}
            label={amountLabel}
            slotProps={{
              input: { startAdornment: <InputAdornment position="start">{adornmentSymbol}</InputAdornment> },
              htmlInput: { min: 0 },
            }}
            data-vy="plan-charge-drawer-discount-amount"
          />

          {isSubscriptionProduct && (
            <DiscountDurationField control={control} recurringPath={recurringPath} />
          )}

          <VyTextFieldField<PlanInputForm>
            name={`${discountConfigPath}.name`}
            label="Discount name"
            placeholder="Will appear on the invoice"
            data-vy="plan-charge-drawer-discount-name"
          />
        </VyStack>
      </VyStack>
    </VyToggleSection>
  );
};

const DiscountSectionWithSuspenseBoundary = withSuspenseBoundary(DiscountSectionInner);

export { DiscountSectionWithSuspenseBoundary as DiscountSection };
